import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { fetchEventWithRelays } from '@/lib/queryEvent';
import { getParentEventId } from '@/lib/postUtils';

/** Cap on how many ancestors we'll walk up (guards against reference loops). */
const MAX_ANCESTORS = 25;
/** Per-hop timeout while resolving a single ancestor. */
const HOP_TIMEOUT_MS = 5000;

/**
 * Fetches the full chain of ancestor posts above a reply, walking from the
 * given event up to the thread root following NIP-10 parent markers.
 *
 * Returns ancestors in chronological order (root first), NOT including the
 * starting event itself. Relay hints from each hop's `e` tag are probed
 * directly, so ancestors living on slower or out-of-pool relays still resolve.
 */
export function useThreadChain(startEvent: NostrEvent | null) {
  const { nostr } = useNostr();
  const startId = startEvent?.id;

  return useQuery<NostrEvent[]>({
    queryKey: ['nostr', 'thread-chain', startId ?? ''],
    queryFn: async ({ signal }) => {
      if (!startEvent) return [];

      const abort = AbortSignal.any([signal, AbortSignal.timeout(30_000)]);
      const chain: NostrEvent[] = [];
      const visited = new Set<string>([startEvent.id]);

      let parentRef = getParentEventId(startEvent);
      let depth = 0;

      while (parentRef && depth < MAX_ANCESTORS) {
        if (visited.has(parentRef.id)) break; // reference loop — bail out
        visited.add(parentRef.id);

        const event = await fetchEventWithRelays(
          nostr,
          [{ ids: [parentRef.id], limit: 1 }],
          {
            relayHints: parentRef.relay ? [parentRef.relay] : undefined,
            timeoutMs: HOP_TIMEOUT_MS,
            signal: abort,
          },
        );

        if (!event) break; // ancestor missing on our relays — chain ends here

        chain.push(event);
        parentRef = getParentEventId(event);
        depth += 1;
      }

      // Reverse so the chain reads top-to-bottom chronologically (root first).
      return chain.reverse();
    },
    enabled: !!startId,
    staleTime: 5 * 60 * 1000, // ancestor chains are immutable once resolved
    retry: 1,
    retryDelay: 1000,
  });
}
